import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { ClinicAudit } from "@/components/landing/ClinicAudit";

/**
 * AuditModal — full-screen popup wrapper around the existing ClinicAudit
 * component, so the "Free Audit" CTA works from every page (not just the
 * /#clinic-audit anchor on Home).
 */
export const AuditModal = ({ open, onClose }) => {
  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Escape key closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="audit-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          data-testid="audit-modal-overlay"
          className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/70 px-3 pb-6 pt-4 backdrop-blur-sm sm:px-6 sm:pt-10"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            data-testid="audit-modal"
            className="relative w-full max-w-5xl overflow-hidden rounded-3xl bg-card shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button — floats over the dark inner card */}
            <button
              type="button"
              onClick={onClose}
              data-testid="audit-modal-close"
              aria-label="Close audit"
              className="absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white shadow-lg backdrop-blur transition-all hover:scale-105 hover:bg-white/20 sm:right-5 sm:top-5"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Render the existing ClinicAudit component, but trim its
                outer section padding via a CSS scope class. */}
            <div className="audit-modal-scope">
              <ClinicAudit />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AuditModal;
